<template>
  <div class="group -flex -wide -vs-center achievement">
    <div class="achievement-info">
      <h1 class="title">{{ achievement.title }}</h1>
      <div
        v-if="achievementType === 'available' && achievement.displayType === 'hidden_criteria'"
        class="criteria"
      >
        <p>Criteria is hidden</p>
      </div>
      <div v-else class="criteria">
        {{ achievement.blurb }}
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div class="message" v-html="achievement.body" />

      <ul class="achievement-tags">
        <li v-for="tag in achievement.tags" :key="tag" class="tag">{{ tag }}</li>
      </ul>
    </div>
    <div class="achievement-details">
      <div :class="`achievement-badge ${levelClassName}`">
        <div class="icon" @touchstart="swipeStart" @touchmove="swipeMove" @touchend="swipeEnd">
          <img v-if="achievement.imageUrl" :src="achievement.imageUrl" alt="" />
          <i v-else class="material-symbols-outlined fill">{{ achievement.imageIcon }}</i>
        </div>
        <div class="level-info">
          <div class="prev-level" :class="isFirstLevel ? ' -inactive' : ''">
            <icon-button
              icon="arrow_back_ios_new"
              classes="level-nav -no-border -ico-only"
              label="Previous Level"
              @click-action="previousLevel"
            />
          </div>
          <div class="current-level">
            <div class="level">Level {{ activeAchievement.level }}</div>
            <div class="date-achieved">
              {{ completeDate }}
            </div>
          </div>
          <div class="next-level" :class="isHighestLevel ? ' -inactive' : ''">
            <icon-button
              icon="arrow_forward_ios"
              classes="level-nav -no-border -ico-only"
              label="Next Level"
              @click-action="nextLevel"
            />
          </div>
        </div>
      </div>
      <div class="achievement-progress">
        <div aria-label="progress percentage" class="progress-bar">
          <div class="progress-total"></div>
          <div class="progress-actual" :style="`width:${progressBarPercentage}%;`"></div>
        </div>
        <div
          v-if="allLevelsComplete || (activeAchievement.completedAt && !isHighestLevel)"
          class="progress-details"
        >
          <div class="level-progress">Completed</div>
          <div class="count">
            {{ activeAchievement.progressCount }} of
            {{
              activeAchievement && activeAchievement.achievementLevel
                ? activeAchievement.achievementLevel.threshold
                : activeAchievement.threshold
            }}
          </div>
        </div>
        <div v-else class="progress-details">
          <div class="level-progress">
            Progress to Level
            {{
              inProgressUserAchievement
                ? inProgressUserAchievement.level
                : inProgressAchievementLevel?.level
            }}
          </div>
          <div class="count">
            {{ inProgressUserAchievement ? inProgressUserAchievement.progressCount : 0 }}
            of
            {{
              inProgressUserAchievement
                ? inProgressUserAchievement.achievementLevel.threshold
                : inProgressAchievementLevel?.threshold
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Individual Achievement component
 */
import { ref, computed, onMounted } from 'vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { useSwipe } from '@/composables/swipe'
import type {
  Achievement,
  AchievementLevel,
  ActiveAchievement,
  UserAchievement,
} from '@/types/achievement-types'

/** PROPS **/
const props = defineProps<{
  achievement: Achievement
  achievementType: 'complete' | 'available'
}>()

/** STATE **/
const { swipeStart, swipeMove, swipeEnd } = useSwipe(handleSwipe)
const allLevelsComplete = ref(true)
const inProgressIndex = ref(0)
const activeIndex = ref(0)
const highestCompleteIndex = ref(0)

/** COMPUTED **/
const activeAchievement = computed<ActiveAchievement>(() => {
  if (props.achievementType === 'complete') {
    return props.achievement.userAchievements[activeIndex.value]
  }

  return props.achievement.achievementLevels[activeIndex.value]
})

const levelClassName = computed(() => {
  if (activeAchievement.value && activeAchievement.value.completedAt) {
    return activeAchievement.value.achievementLevel?.levelStyle
  }

  return '-lvl000'
})

const isFirstLevel = computed(() => activeIndex.value === 0)

const isHighestLevel = computed(() => activeIndex.value === highestCompleteIndex.value)

const completeDate = computed(() => {
  if (activeAchievement.value.completedAt) {
    return activeAchievement.value.completedAtUserTz
  }

  return 'In Progress'
})

const progressBarPercentage = computed<number>(() => {
  let progressPercent = 0

  if (inProgressUserAchievement.value && inProgressUserAchievement.value.progressCount) {
    progressPercent =
      (inProgressUserAchievement.value.progressCount /
        inProgressUserAchievement.value.achievementLevel.threshold) *
      100

    progressPercent = progressPercent > 100 ? 100 : progressPercent
  }

  if (activeAchievement.value.completedAt && !isHighestLevel.value) {
    progressPercent = 100
  }

  return progressPercent
})

const inProgressUserAchievement = computed<UserAchievement | null>(() => {
  if (props.achievement && props.achievement.userAchievements[inProgressIndex.value]) {
    return props.achievement.userAchievements[inProgressIndex.value]
  }

  return null
})

const inProgressAchievementLevel = computed<AchievementLevel | null>(() => {
  if (props.achievement && props.achievement.achievementLevels[inProgressIndex.value]) {
    return props.achievement.achievementLevels[inProgressIndex.value]
  }

  return null
})

/** LIFECYCLE **/
onMounted(() => {
  // were looping through the userAchievements to find both our highest
  // completed and uncompleted userAchievement indices, setting the highest
  // completed as the default to view
  if (props.achievementType === 'complete') {
    props.achievement.userAchievements.forEach((userAchievement, index) => {
      if (userAchievement.completedAt && userAchievement.level > highestCompleteIndex.value) {
        highestCompleteIndex.value = index
      }

      if (!userAchievement.completedAt) {
        inProgressIndex.value = userAchievement.level > inProgressIndex.value ? index : 0
        allLevelsComplete.value = false
      }
    })

    if (allLevelsComplete.value) {
      inProgressIndex.value = highestCompleteIndex.value
    }

    activeIndex.value = highestCompleteIndex.value
  }

  if (props.achievementType === 'available') {
    allLevelsComplete.value = false
    highestCompleteIndex.value = 0
    inProgressIndex.value = 0
    activeIndex.value = 0
  }
})

/** METHODS **/
function previousLevel() {
  if (!isFirstLevel.value) {
    activeIndex.value--
  }
}

function nextLevel() {
  if (!isHighestLevel.value) {
    activeIndex.value++
  }
}

function handleSwipe({ direction }: { direction: string }) {
  if (direction === 'left') {
    nextLevel()
    return
  }
  // right
  previousLevel()
}
</script>
